import {
	AI_RESPONSE_EVENT_NAMES,
	DOCUMENT_CHANGED_EVENT_NAMES,
	INSPIRATION_EVENT_NAMES,
	JOURNAL_EVENT_NAMES,
	MEMBERSHIP_EVENT_NAMES,
	PROJECT_EVENT_NAMES,
	PROPERTY_EVENT_NAMES,
	type RealtimeMessage,
	TOPIC_EVENT_NAMES,
	VISUALIZATION_EVENT_NAMES,
} from "@zplanem/responses"
import { useEffect } from "react"
import { getPusherClient } from "../realtime/pusher-client"

const PROPERTY_CHANNEL_EVENTS = [
	...AI_RESPONSE_EVENT_NAMES,
	...MEMBERSHIP_EVENT_NAMES,
	...PROPERTY_EVENT_NAMES,
	...JOURNAL_EVENT_NAMES,
	...INSPIRATION_EVENT_NAMES,
	...VISUALIZATION_EVENT_NAMES,
	...TOPIC_EVENT_NAMES,
	...PROJECT_EVENT_NAMES,
	...DOCUMENT_CHANGED_EVENT_NAMES,
] as const

/**
 * Subscribe to `private-property-<propertyId>` and dispatch each
 * recognized event to `onMessage`. Cleans up the subscription + handlers
 * on unmount or when `propertyId` changes.
 *
 * Each event in the `RealtimeMessage` union must be bound explicitly —
 * pusher-js's `bind_global` also receives system events (`pusher:*`)
 * which we don't want to route through application code.
 */
export function usePropertyChannel(
	propertyId: string | undefined,
	onMessage: (msg: RealtimeMessage) => void,
): void {
	useEffect(() => {
		if (!propertyId) return
		const channelName = `private-property-${propertyId}`
		const channel = getPusherClient().subscribe(channelName)

		const handlers: Record<string, (data: unknown) => void> = {}
		for (const eventName of PROPERTY_CHANNEL_EVENTS) {
			const handler = (data: unknown) => onMessage(data as RealtimeMessage)
			handlers[eventName] = handler
			channel.bind(eventName, handler)
		}

		return () => {
			for (const [eventName, handler] of Object.entries(handlers)) {
				channel.unbind(eventName, handler)
			}
			getPusherClient().unsubscribe(channelName)
		}
	}, [propertyId, onMessage])
}
